import {
  CarouselLayout,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
} from "@livekit/components-react";
import { isTrackReference } from "@livekit/components-core";
import { Track } from "livekit-client";
import { type ReactNode } from "react";

interface FocusVideoConferenceProps {
  focusedContent?: ReactNode;
  signedNotification?: ReactNode;
}

/**
 * Focused room layout that prioritizes screen-share and falls back to camera.
 */
export const FocusVideoConference = ({
  focusedContent,
  signedNotification,
}: FocusVideoConferenceProps) => {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const focusedTrack =
    tracks.find(
      (track) =>
        isTrackReference(track) &&
        track.publication.source === Track.Source.ScreenShare &&
        track.publication.isSubscribed,
    ) ??
    tracks.find((track) => !track.participant.isLocal) ??
    null;

  const carouselTracks = tracks.filter((track) => track !== focusedTrack);

  if (focusedContent) {
    return (
      <>
        <RoomAudioRenderer />
        <FocusLayoutContainer>
          <CarouselLayout tracks={tracks}>
            <ParticipantTile />
          </CarouselLayout>
          <div className="w-full h-full rounded-md">{focusedContent}</div>
        </FocusLayoutContainer>
      </>
    );
  }

  return (
    <>
      <RoomAudioRenderer />
      {!focusedTrack ? (
        <GridLayout tracks={tracks}>
          <ParticipantTile />
        </GridLayout>
      ) : (
        <FocusLayoutContainer>
          <CarouselLayout tracks={carouselTracks}>
            <ParticipantTile />
          </CarouselLayout>
          <div className="relative">
            <FocusLayout trackRef={focusedTrack} />
            {signedNotification}
          </div>
        </FocusLayoutContainer>
      )}
    </>
  );
};
